import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Shield, Play, CheckCircle, ShoppingBag, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import { API_URL } from '../config';
import '../styles/Dashboard.css';


const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const purchaseSuccess = searchParams.get('purchase') === 'success';

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard`, {
          withCredentials: true
        });
        setDashboardData(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboard();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <Header />
        <div className="dashboard-loading">
          <Loader2 size={48} className="spinner" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Header />
      
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!</h1>
          <p>Continue learning and stay secure</p>
        </div>
        
        {purchaseSuccess && (
          <div className="purchase-success-banner">
            <CheckCircle size={24} />
            <div>
              <strong>Purchase successful!</strong>
              <p>Your course is now available below.</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="dashboard-error">
            <p>{error}</p>
          </div>
        )}
        
        <section className="my-courses-section">
          <h2>
            <Shield size={24} />
            My Courses
          </h2>
          
          {dashboardData?.purchased_courses?.length > 0 ? (
            <div className="my-courses-grid">
              {dashboardData.purchased_courses.map(({ course, purchased_at }) => (
                <div key={course.id} className="my-course-card">
                  <div className="my-course-header">
                    <Shield size={32} />
                    <span className="owned-badge">
                      <CheckCircle size={14} />
                      Owned
                    </span>
                  </div>
                  
                  <h3>{course.title}</h3>
                  <p className="course-description">{course.short_description}</p>
                  
                  <div className="purchase-date">
                    Purchased on {formatDate(purchased_at)}
                  </div>
                  
                  <Link 
                    to={`/my-courses/${course.slug}`} 
                    className="btn-continue"
                  >
                    <Play size={18} />
                    Continue Learning
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-courses-message">
              <ShoppingBag size={64} />
              <h3>No courses yet</h3>
              <p>Start your cybersecurity journey today</p>
              <Link to="/courses" className="btn-browse">
                Browse Courses
              </Link>
            </div>
          )}
        </section>
        
        {dashboardData?.purchased_courses?.length > 0 && (
          <div className="browse-more">
            <Link to="/courses">Browse More Courses →</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
